import { motion } from "framer-motion";
import whoweareData from "~/data/about-us/whoweare.json";
import { useFontStyle } from "~/hook/useFontStyle";
import useIntersectionObserver from "~/hook/useIntersectionObserver";
import { cn } from "~/lib/utils";

const ContentSection = ({
  title,
  content,
  imageSrc,
  imageAlt,
  index,
}: {
  title: string;
  content: string;
  imageSrc: string;
  imageAlt: string;
  index: number;
}) => {
  const [ref, isVisible] = useIntersectionObserver();
  const { normalInter, medium } = useFontStyle();
  const isEvenIndex = index % 2 === 0;

  const containerVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" },
    },
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { delay: 0.2, duration: 0.7, ease: "easeOut" },
    },
  };

  return (
    <motion.section ref={ref} initial="hidden" animate={isVisible ? "visible" : "hidden"} variants={containerVariants} className="mb-16">
      <div className={cn("grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center", isEvenIndex && "md:grid-flow-dense")}>
        {/* Image */}
        <motion.div
          variants={imageVariants}
          className={cn("overflow-hidden rounded-2xl shadow-xl border-2 border-primaryColor/20", isEvenIndex && "md:order-2")}
        >
          <motion.img
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.4 }}
            src={imageSrc}
            alt={imageAlt}
            className="w-full h-auto object-cover rounded-lg"
          />
        </motion.div>

        {/* Content */}
        <motion.div
          variants={{
            hidden: { opacity: 0, x: isEvenIndex ? -30 : 30 },
            visible: {
              opacity: 1,
              x: 0,
              transition: { delay: 0.1, duration: 0.7, ease: "easeOut" },
            },
          }}
          className={cn("space-y-4", isEvenIndex && "md:order-1")}
        >
          {/* Title with icon background */}
          <div className="flex items-start gap-4">
            <motion.div
              whileHover={{ rotate: 360 }}
              transition={{ duration: 0.6 }}
              className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-primaryColor to-primaryColor/70 rounded-xl flex items-center justify-center shadow-lg"
            >
              <div className="text-2xl text-white">✨</div>
            </motion.div>
            <h3 className={cn("text-2xl md:text-3xl font-bold text-textColor pt-2", medium)}>{title}</h3>
          </div>

          {/* Description */}
          <p className={cn("text-textSecondaryColor text-base md:text-lg leading-relaxed text-justify", normalInter)}>{content}</p>

          {/* Decorative accent line */}
          <motion.div
            initial={{ scaleX: 0 }}
            animate={isVisible ? { scaleX: 1 } : { scaleX: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="origin-left w-24 h-1 bg-gradient-to-r from-primaryColor to-primaryColor/40 rounded-full mt-6"
          />
        </motion.div>
      </div>
    </motion.section>
  );
};

// Hero Introduction Section
const HeroIntroduction = () => {
  const [ref, isVisible] = useIntersectionObserver();
  const { normalInter, medium } = useFontStyle();

  const titleVariants = {
    hidden: { opacity: 0, y: -30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" },
    },
  };

  const contentVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { delay: 0.3, duration: 0.8, ease: "easeOut" },
    },
  };

  return (
    <motion.section ref={ref} initial="hidden" animate={isVisible ? "visible" : "hidden"} className="relative w-full mb-16 md:mb-28 px-4 md:px-6">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-primaryColor/5 via-transparent to-primaryColor/5 rounded-3xl -z-10" />

      <div className="max-w-5xl mx-auto">
        <motion.div variants={titleVariants} className="text-center mb-8 md:mb-12">
          <motion.h1
            initial={{ scale: 0.9, opacity: 0 }}
            animate={isVisible ? { scale: 1, opacity: 1 } : { scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.7 }}
            className={cn("text-2xl md:text-5xl lg:text-5xl font-bold text-textColor mb-4", medium)}
          >
            Sadguru Shree Gyanjivandasji Swami
          </motion.h1>

          <motion.div
            initial={{ scaleX: 0 }}
            animate={isVisible ? { scaleX: 1 } : { scaleX: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="w-32 h-1.5 bg-gradient-to-r from-transparent via-primaryColor to-transparent mx-auto mb-8"
          />

          <motion.p variants={contentVariants} className={cn("text-lg md:text-xl text-primaryColor italic font-medium", medium)}>
            Spiritual Mentor • Cultural Preserver • Social Servant
          </motion.p>
        </motion.div>

        {/* Introduction content */}
        <motion.div
          variants={{
            hidden: { opacity: 0, y: 20 },
            visible: {
              opacity: 1,
              y: 0,
              transition: { delay: 0.4, duration: 0.8 },
            },
          }}
          className="bg-white rounded-2xl shadow-lg border-2 border-primaryColor/20 p-8 md:p-12"
        >
          <p className={cn("text-textSecondaryColor text-base md:text-lg leading-relaxed text-justify", normalInter)}>
            {whoweareData.content.introduction}
          </p>
        </motion.div>
      </div>
    </motion.section>
  );
};

// Main Component
function GyanjivanDasjiSwami() {
  const { normalInter } = useFontStyle();

  const pageVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={pageVariants}
      // className="w-full min-h-screen bg-gradient-to-br from-secondaryColor/20 via-white to-secondaryColor/10 "
    >
      {/* Hero Introduction */}
      <HeroIntroduction />

      {/* Content Sections Container */}
      <motion.div className="max-w-6xl mx-auto px-4">
        {whoweareData.content.sections.map((section, index) => (
          <ContentSection
            key={index}
            title={section.title}
            content={section.content}
            imageSrc={section.imageSrc}
            imageAlt={section.imageAlt}
            index={index}
          />
        ))}
      </motion.div>
    </motion.div>
  );
}

export default GyanjivanDasjiSwami;
